import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { pricingPlans } from '../data/mockData';
import { Check } from 'lucide-react';

const Pricing = () => {
  return (
    <section id="pricing" className="py-24 bg-[#37464F] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#009688] to-transparent opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Spa, Hospitality, and Venue Pricing
          </h2>
          <p className="text-lg text-gray-400">
            All plans include unlimited Guests, Vendors, &amp; Products
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {pricingPlans.map((plan, index) => (
            <Card
              key={index}
              className={`relative overflow-hidden transition-all duration-300 ${
                plan.popular
                  ? 'bg-gradient-to-b from-[#009688]/10 to-[#101F27] border-[#009688] scale-105 shadow-2xl shadow-[#009688]/20'
                  : 'bg-[#101F27]/80 border-gray-800 hover:border-[#009688]/50'
              }`}
            >
              <CardContent className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-white mb-4 uppercase tracking-wider">{plan.name}</h3>
                  
                  {/* Price */}
                  <div className="mb-4 min-h-[100px] flex flex-col justify-center">
                    {plan.name === 'Enterprise' ? (
                      <div className="text-4xl font-bold text-[#009688]">{plan.price}</div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500 mb-1">starting at</p>
                        <div className="flex items-baseline justify-center">
                          <span className="text-2xl text-white">US$</span>
                          <span className="text-5xl font-bold text-[#009688]">
                            {plan.price.replace('$', '').split('.')[0]}
                          </span>
                          <span className="text-3xl text-[#009688]">.{plan.price.split('.')[1]}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{plan.period}</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Features Section Header */}
                <div className="bg-[#009688] text-white text-center py-3 rounded-lg mb-6 font-bold">
                  {plan.name} Features
                </div>

                {/* Description */}
                {plan.description && index !== 0 && (
                  <div className="text-center mb-4">
                    <p className="text-sm font-semibold text-gray-400">{plan.description}</p>
                  </div>
                )}

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="text-center">
                      {feature.startsWith('→') ? (
                        <span className="text-gray-400 text-sm pl-4">{feature}</span>
                      ) : feature.endsWith(':') ? (
                        <span className="text-gray-300 text-sm font-semibold">{feature}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">{feature}</span>
                      )}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  className={`w-full ${
                    plan.popular
                      ? 'bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white shadow-lg shadow-[#009688]/30'
                      : 'border-[#009688] text-[#009688] hover:bg-[#009688]/10'
                  }`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => {
                    const element = document.getElementById('demo');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Events & Festivals CTA Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <Card className="bg-gradient-to-r from-[#37464F] to-[#37464F] border-[#009688]/30">
            <CardContent className="p-8 text-center">
              <h3 className="text-3xl font-bold text-white mb-4">
                Hosting an Event or Festival?
              </h3>
              <p className="text-gray-400 text-lg mb-6">
                Our event pricing is tailored based on scale and requirements. Get in touch to learn more.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#009688] to-[#005B4F] hover:from-[#005B4F] hover:to-[#009688] text-white text-lg px-8 py-6 shadow-lg shadow-[#009688]/30"
                onClick={() => {
                  const element = document.getElementById('demo');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Request Event Pricing
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-6">
            All plans include bank-grade security, real-time reporting, and dedicated support
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-[#009688]" />
              <span className="text-gray-400">No Setup Fees</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-[#009688]" />
              <span className="text-gray-400">No Long-Term Contracts</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-[#009688]" />
              <span className="text-gray-400">Cancel Anytime</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
